import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import AddIcon from "@mui/icons-material/Add";
import api from "../../api";
import LibraryPickerDrawer from "../ContentLibrary/LibraryPickerDrawer";
import * as bankApi from "./contentBankApi";
import { formatBytes, formatDate, plural } from "./format";

// ─── Confirm ────────────────────────────────────────────────────────────────

export const ConfirmDialog = ({ open, title, body, confirmLabel = "Delete", danger = true, busy, onConfirm, onClose }) => (
  <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
    <DialogContent>
      <Typography sx={{ color: "#374151" }}>{body}</Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} disabled={busy}>Cancel</Button>
      <Button variant="contained" color={danger ? "error" : "primary"} onClick={onConfirm} disabled={busy}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Text prompt (new module, rename) ───────────────────────────────────────

export const TextPromptDialog = ({ open, title, label, initial = "", confirmLabel = "Save", note, busy, onSubmit, onClose }) => {
  const [value, setValue] = useState(initial);
  useEffect(() => { if (open) setValue(initial); }, [open, initial]);
  const submit = (event) => {
    event.preventDefault();
    if (value.trim()) onSubmit(value.trim());
  };
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label={label} value={value} sx={{ mt: 1 }}
            onChange={(e) => setValue(e.target.value)} />
          {note && <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 1.5 }}>{note}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy || !value.trim()}>{confirmLabel}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ─── Version history ────────────────────────────────────────────────────────

export const VersionsDialog = ({ chapter, onClose, onRestored }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (!chapter) return;
    setData(null);
    setError("");
    bankApi.getVersions(chapter.id).then(setData).catch((err) => setError(bankApi.errorMessage(err)));
  }, [chapter]);

  const restore = async (version) => {
    setRestoring(version);
    setError("");
    try {
      await bankApi.restoreVersion(chapter.id, version);
      onRestored();
    } catch (err) {
      setError(bankApi.errorMessage(err, "Couldn't restore that version"));
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Dialog open={!!chapter} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Version history
        <Typography sx={{ fontSize: 13, color: "#6B7280" }}>{chapter?.title}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!data && !error && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>}
        {data?.versions.length === 0 && <Typography color="text.secondary">No versions recorded yet.</Typography>}
        {data?.versions.map((v) => {
          const current = v.version === data.current;
          return (
            <Box key={v.version} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.25, borderBottom: "1px solid #F1F5F9" }}>
              <Chip size="small" label={`v${v.version}`} color={current ? "primary" : "default"} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }} noWrap>
                  {v.html_filename} {current && <Box component="span" sx={{ color: "#0172A6", fontSize: 12 }}>· current</Box>}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#6B7280" }} noWrap>
                  {formatDate(v.published_at)}{v.published_by ? ` · ${v.published_by}` : ""}
                  {v.assets_count ? ` · ${plural(v.assets_count, "asset")}` : ""}
                  {v.total_size_bytes ? ` · ${formatBytes(v.total_size_bytes)}` : ""}
                  {v.note ? ` · ${v.note}` : ""}
                </Typography>
              </Box>
              {!current && (
                <Button size="small" startIcon={restoring === v.version ? <CircularProgress size={14} /> : <RestoreIcon />}
                  disabled={!!restoring} onClick={() => restore(v.version)}>
                  Restore
                </Button>
              )}
            </Box>
          );
        })}
        <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 2 }}>
          Restoring makes that version current again (recorded as a new version); nothing is deleted.
        </Typography>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

// ─── Copy chapters from another course/batch ────────────────────────────────

export const CopyChaptersDialog = ({ target, onClose, onCopied }) => {
  const [type, setType] = useState("1:1");
  const [sources, setSources] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!target) return;
    setCourseId("");
    setModules([]);
    setModuleId("");
    setError("");
    bankApi.getSources(type).then((d) => setSources(d.items)).catch((err) => setError(bankApi.errorMessage(err)));
  }, [target, type]);

  useEffect(() => {
    setModuleId("");
    if (!courseId) { setModules([]); return; }
    bankApi.getCourse(courseId).then((d) => setModules(d.modules)).catch((err) => setError(bankApi.errorMessage(err)));
  }, [courseId]);

  const source = modules.find((m) => m.id === moduleId);
  const copyable = source ? source.chapters.filter((c) => !c.hidden && c.has_file) : [];

  const copy = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await bankApi.copyChapters(target.id, moduleId);
      onCopied(result.copied);
    } catch (err) {
      setError(bankApi.errorMessage(err, "Couldn't copy the chapters"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Copy chapters into “{target?.name}”
        <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
          The copies use the same files; editing either side later doesn't change the other.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <ToggleButtonGroup size="small" exclusive value={type} onChange={(_, v) => v && setType(v)} sx={{ mb: 2 }}>
          <ToggleButton value="1:1">1:1 courses</ToggleButton>
          <ToggleButton value="cohort">Cohort batches</ToggleButton>
        </ToggleButtonGroup>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>{type === "cohort" ? "Batch" : "Course"}</InputLabel>
          <Select label={type === "cohort" ? "Batch" : "Course"} value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            {sources.map((s) => (
              <MenuItem key={`${s.type}-${s.batch_id || s.course_id}`} value={s.course_id}>
                {s.name}{s.status_label ? ` · ${s.status_label}` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small" disabled={!courseId}>
          <InputLabel>Module</InputLabel>
          <Select label="Module" value={moduleId} onChange={(e) => setModuleId(e.target.value)}>
            {modules.map((m) => (
              <MenuItem key={m.id} value={m.id} disabled={m.id === target?.id}>
                {m.name} ({plural(m.chapters.length, "chapter")})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {source && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              {copyable.length ? `Will copy ${plural(copyable.length, "chapter")}:` : "Nothing to copy: this module has no visible chapters."}
            </Typography>
            {copyable.map((c) => (
              <Typography key={c.id} sx={{ fontSize: 13, color: "#374151" }}>• {c.title}</Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={busy || !copyable.length} onClick={copy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
          Copy chapters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Recently deleted ───────────────────────────────────────────────────────

export const TrashDialog = ({ open, course, onClose, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const chapters = (course?.modules || []).flatMap((m) =>
    m.deleted_chapters.map((c) => ({ ...c, moduleName: m.name })));
  const modules = course?.deleted_modules || [];

  const restore = async (kind, id) => {
    setBusy(`${kind}-${id}`);
    setError("");
    try {
      if (kind === "module") await bankApi.restoreModule(id);
      else await bankApi.restoreChapter(id);
      await onChanged();
    } catch (err) {
      setError(bankApi.errorMessage(err, "Couldn't restore it"));
    } finally {
      setBusy(null);
    }
  };

  const row = (kind, id, title, subtitle) => (
    <Box key={`${kind}-${id}`} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1, borderBottom: "1px solid #F1F5F9" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }} noWrap>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12, color: "#6B7280" }} noWrap>{subtitle}</Typography>}
      </Box>
      <Button size="small" startIcon={busy === `${kind}-${id}` ? <CircularProgress size={14} /> : <RestoreIcon />}
        disabled={!!busy} onClick={() => restore(kind, id)}>Restore</Button>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Recently deleted</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!modules.length && !chapters.length && <Typography color="text.secondary">Nothing deleted.</Typography>}
        {modules.length > 0 && <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#6B7280", mt: 1 }}>MODULES</Typography>}
        {modules.map((m) => row("module", m.id, m.name, "Restoring brings it back for students too"))}
        {chapters.length > 0 && <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#6B7280", mt: 2 }}>CHAPTERS</Typography>}
        {chapters.map((c) => row("chapter", c.id, c.title, `in ${c.moduleName}`))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

// ─── Questions & assignments linked to a module ─────────────────────────────

const PRACTICE_TABS = [
  { key: "assessments", label: "Questions" },
  { key: "assignments", label: "Assignments" },
];

export const PracticeDialog = ({ module, sourceName, students, isCohort, onClose }) => {
  const [tab, setTab] = useState(0);
  const [content, setContent] = useState({ assessments: [], assignments: [] });
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState("");
  const kind = PRACTICE_TABS[tab].key;

  const load = async () => {
    if (!module) return;
    setLoading(true);
    try {
      const { data } = await api.get(`api/course-template/${module.id}/content/`);
      setContent({ assessments: data.assessments || [], assignments: data.assignments || [] });
    } catch (err) {
      setError(bankApi.errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (module) { setTab(0); setError(""); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  const remove = async (item) => {
    try {
      await api.delete(`api/course-template/${module.id}/content/${item.type}/${item.id}/`);
      load();
    } catch (err) {
      setError(bankApi.errorMessage(err, "Couldn't remove it"));
    }
  };

  const items = content[kind];
  return (
    <>
      <Dialog open={!!module && !pickerOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Questions & assignments
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>{module?.name}</Typography>
        </DialogTitle>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: "1px solid #E5E7EB" }}>
          {PRACTICE_TABS.map((t) => (
            <Tab key={t.key} label={`${t.label} (${content[t.key].length})`} sx={{ textTransform: "none" }} />
          ))}
        </Tabs>
        <DialogContent sx={{ minHeight: 220 }}>
          {!isCohort && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="one-to-one-note">
              A 1:1 student's questions and assignments belong to them alone: add them on the student's own module
              card (Task Lists → Manage assessments / Manage assignments). Nothing here reaches 1:1 students.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>}
          {!loading && !items.length && (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
              Nothing linked yet. Add from the {kind === "assessments" ? "question" : "assignment"} library.
            </Typography>
          )}
          {!loading && items.map((item) => (
            <Box key={`${item.type}-${item.id}`} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: "1px solid #F1F5F9" }}>
              <Chip size="small" label={item.type} />
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: 14 }} noWrap>{item.title}</Typography>
              {isCohort && (
                <Tooltip title="Remove from module">
                  <IconButton size="small" onClick={() => remove(item)} aria-label="Remove"><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          {isCohort ? <Button startIcon={<AddIcon />} onClick={() => setPickerOpen(true)}>Add from library</Button> : <span />}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      {module && (
        <LibraryPickerDrawer
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          topicTemplateId={module.id}
          topicName={module.name}
          batchName={sourceName}
          contentType={kind}
          isBatch={isCohort}
          studentCount={students}
          assignedIds={items.map((item) => `${item.type}-${item.id}`)}
          onAssignComplete={() => { setPickerOpen(false); load(); }}
        />
      )}
    </>
  );
};
